import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Check, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { fmtDT } from "@/lib/format";

const MODES = [
  ["opening_stock", "Opening Stock", "Initial migration — qty + unit cost creates FIFO cost layers"],
  ["stock_count", "Physical Stock Count", "Counted qty vs system → variance posted after approval"],
  ["adjustment", "Bulk Adjustment", "Positive or negative qty with mandatory reason"],
  ["location_transfer", "Location Transfer", "Bulk location → location moves, validated at approval"],
];
const MGR = ["admin", "warehouse_manager"];

export default function UploadTab() {
  const { user: me } = useAuth();
  const [mode, setMode] = useState("opening_stock");
  const [preview, setPreview] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);

  const isMgr = MGR.includes(me?.role);

  const loadUploads = useCallback(() => {
    if (isMgr) api.get("/stock/uploads").then((r) => setUploads(r.data)).catch(() => {});
  }, [isMgr]);
  useEffect(() => { loadUploads(); }, [loadUploads]);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/stock/upload/preview?mode=${mode}`, fd);
      setPreview({ ...r.data, _file: f.name });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const commit = async () => {
    if (!preview?.valid?.length) return;
    try {
      const r = await api.post("/stock/upload/commit", {
        rows: preview.valid, mode, file_name: preview._file, errors: preview.invalid || [],
      });
      toast.success(`${r.data.upload_id} submitted for approval — no stock changed yet`);
      setPreview(null);
      loadUploads();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Commit failed");
    }
  };

  const decide = async (u, action) => {
    const body = action === "reject" ? { reason: window.prompt("Rejection reason:") || "rejected" } : {};
    try {
      const r = await api.post(`/stock/uploads/${u.upload_id}/${action}`, body);
      toast.success(action === "approve" ? `Approved — ${r.data.posted_rows} rows posted to inventory ledger` : "Upload rejected");
      loadUploads();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="upload-tab">
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide">Bulk Inventory Upload</p>
            <p className="text-xs text-muted-foreground">Validate → preview → submit → manager approval → posted as inventory transactions. Excel can never overwrite stock directly.</p>
          </div>
          <Button variant="outline" size="sm" data-testid="download-template"
            onClick={() => window.open(`${API}/stock/upload/template?mode=${mode}`, "_blank")}>
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Download Template
          </Button>
        </div>
        <div className="grid sm:grid-cols-4 gap-2" data-testid="upload-modes">
          {MODES.map(([k, label, hint]) => (
            <button key={k} onClick={() => { setMode(k); setPreview(null); }} data-testid={`mode-${k}`}
              className={`text-left border rounded-lg p-3 transition-colors ${mode === k ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
              <p className="text-xs font-bold uppercase tracking-wide">{label}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground cursor-pointer text-sm font-medium" data-testid="upload-pick">
          <Upload className="w-4 h-4" /> {busy ? "Validating…" : "Choose CSV/XLSX & preview"}
          <input type="file" accept=".csv,.xlsx" className="hidden" onChange={pick} data-testid="upload-file-input" />
        </label>
      </div>

      {preview && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="upload-preview">
          <p className="text-sm">
            <span className="metric font-bold">{preview.total_rows}</span> rows ·{" "}
            <span className="text-signal-green font-bold">{preview.valid.length} valid</span> ·{" "}
            <span className={preview.invalid.length ? "text-signal-red font-bold" : ""}>{preview.invalid.length} errors</span>
            <span className="text-muted-foreground text-xs"> · mode: {preview.mode.replace(/_/g, " ")} · {preview._file}</span>
          </p>
          {preview.invalid.length > 0 && (
            <div className="border border-signal-red/40 rounded-md p-3 space-y-1" data-testid="upload-errors">
              <p className="text-xs font-bold text-signal-red uppercase tracking-wide">Validation failed rows (fix and re-upload — nothing posts with errors):</p>
              {preview.invalid.slice(0, 10).map((e) => (
                <p key={e.row} className="text-xs font-mono text-signal-red">Row {e.row}: {e.errors.join("; ")}</p>
              ))}
              {preview.invalid.length > 10 && <p className="text-xs text-muted-foreground">…and {preview.invalid.length - 10} more</p>}
            </div>
          )}
          <div className="max-h-56 overflow-auto border border-border rounded-md">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border text-left">
                {["SKU", "Qty", "Unit Cost", "Location", "To Loc", "Batch", "Reason"].map((h) => (
                  <th key={h} className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
              </tr></thead>
              <tbody>
                {preview.valid.slice(0, 50).map((r, i) => (
                  <tr key={`${r.sku}-${r.bin || r.location || i}`} className="border-b border-border/50">
                    <td className="px-3 py-1.5 font-mono">{r.sku}</td>
                    <td className="px-3 py-1.5 metric">{r.quantity}</td>
                    <td className="px-3 py-1.5 metric">{r.unit_cost ?? "—"}</td>
                    <td className="px-3 py-1.5">{r.location || "—"}</td>
                    <td className="px-3 py-1.5">{r.to_location || "—"}</td>
                    <td className="px-3 py-1.5">{r.batch || "—"}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{r.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button onClick={commit} disabled={!preview.valid.length || preview.invalid.length > 0}
            className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="upload-commit">
            Submit {preview.valid.length} rows for approval
          </Button>
          {preview.invalid.length > 0 && <p className="text-xs text-signal-amber">Resolve all error rows before submitting (no partial posts).</p>}
        </div>
      )}

      {isMgr && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-2" data-testid="upload-approvals">
          <p className="text-sm font-semibold uppercase tracking-wide">Upload Approval Queue <span className="text-[10px] font-mono text-muted-foreground normal-case">(maker-checker: you cannot approve your own upload)</span></p>
          {uploads.length === 0 && <p className="text-sm text-muted-foreground">No uploads yet.</p>}
          {uploads.map((u) => (
            <div key={u.upload_id} className="flex items-center gap-3 border border-border rounded-md p-3 flex-wrap" data-testid={`upload-row-${u.upload_id}`}>
              <span className="font-mono text-xs text-primary font-bold">{u.upload_id}</span>
              <span className="text-xs uppercase font-mono">{u.mode.replace(/_/g, " ")}</span>
              <span className="text-xs text-muted-foreground">{u.file_name || "—"}</span>
              <span className="text-xs metric">{u.valid_rows} rows{u.error_rows ? ` · ${u.error_rows} errors` : ""}</span>
              <span className="text-xs text-muted-foreground">by {u.uploaded_by} · {fmtDT(u.created_at)}</span>
              <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${u.status === "approved" ? "bg-signal-green/15 text-signal-green" : u.status === "rejected" ? "bg-signal-red/15 text-signal-red" : "bg-signal-amber/15 text-signal-amber"}`} data-testid={`upload-status-${u.upload_id}`}>
                {u.status.replace(/_/g, " ")}{u.approver ? ` · ${u.approver}` : ""}
              </span>
              <div className="ml-auto flex gap-1">
                {u.error_rows > 0 && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`upload-errors-${u.upload_id}`}
                    onClick={() => window.open(`${API}/stock/uploads/${u.upload_id}/errors`, "_blank")}>
                    <Download className="w-3 h-3 mr-1" /> Error report
                  </Button>
                )}
                {u.status === "pending_approval" && (
                  <>
                    <Button size="sm" className="h-7 bg-signal-green/20 text-signal-green hover:bg-signal-green/30" data-testid={`upload-approve-${u.upload_id}`} onClick={() => decide(u, "approve")}><Check className="w-3 h-3 mr-1" /> Approve & Post</Button>
                    <Button size="sm" className="h-7 bg-signal-red/20 text-signal-red hover:bg-signal-red/30" data-testid={`upload-reject-${u.upload_id}`} onClick={() => decide(u, "reject")}><X className="w-3 h-3 mr-1" /> Reject</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
